import { useCallback, useMemo } from 'react';
import { matchPath } from 'react-router-dom';
import {
  buildQueryFilters,
  PermissionAction,
  PermissionResource,
  useGetChildOrganizations,
  useGetOrganization,
} from '@sib/api';
import { DEFAULT_PAGE, MAXIMUM_PAGINATION } from '@sib/api/constants';
import { useCan, useUserSetupStore } from '@sib/auth';
import { usePowerBiReports } from '@sib/ui/reports';
import { ABOUTSIB_URL } from '@spendbrain/config/constants';
import { paths } from '@spendbrain/config/paths';

export const useSpendbrainRoutesAccess = () => {
  const { powerBiReports, isLoading: arePowerBiReportsLoading } =
    usePowerBiReports();
  const showReportsDirectory = powerBiReports.length > 0;
  const canAccess = useCan();
  const {
    organization: { id: organizationId },
    hasGlobalAccess,
  } = useUserSetupStore();

  const ticketPaths = hasGlobalAccess
    ? paths.tickets.employee
    : paths.tickets.client;
  const {
    data: organizationData,
    isLoading,
    isPending,
  } = useGetOrganization(organizationId);

  const isOrganizationDataLoading = isLoading || isPending;

  const hasVectorClientId = !!organizationData?.vectorClientId;
  const hasNetsuiteCustomerId = !!organizationData?.netsuiteCustomerId;
  const hasBillingAccess = hasVectorClientId || hasNetsuiteCustomerId;

  const { data: childOrgsData } = useGetChildOrganizations(
    organizationId || '',
    {
      page: DEFAULT_PAGE,
      size: MAXIMUM_PAGINATION,
      filter: buildQueryFilters([
        { field: 'micrositesEnabled', operator: 'eq', value: true },
      ]),
    }
  );

  const hasChildren = (childOrgsData?.totalItems ?? 0) > 0;

  const availableRoutes = useMemo(
    () =>
      [
        ...(hasChildren
          ? [
              {
                href: paths.subsidiaryOrganization,
              },
            ]
          : []),
        ...(canAccess({
          permissions: [
            {
              actions: PermissionAction.READ,
              resources: PermissionResource.SpendAreas,
            },
            {
              actions: PermissionAction.READ,
              resources: PermissionResource.ActionItems,
            },
            {
              actions: PermissionAction.CREATE,
              resources: PermissionResource.SpendAreas,
            },
          ],
          operator: 'OR',
        })
          ? [
              {
                href: paths.dashboard,
                access: {
                  permissions: {
                    actions: PermissionAction.READ,
                    resources: PermissionResource.Dashboard,
                  },
                },
              },
            ]
          : []),
        {
          href: paths.savings,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Savings,
            },
          },
        },
        {
          href: paths.services.root,
          access: {
            permissions: [
              {
                actions: PermissionAction.READ,
                resources: PermissionResource.SpendAreas,
              },
              {
                actions: PermissionAction.READ,
                resources: PermissionResource.ActionItems,
              },
              {
                actions: PermissionAction.CREATE,
                resources: PermissionResource.SpendAreas,
              },
            ],
            operator: 'OR',
          },
        },
        {
          href: paths.services.tab,
          access: {
            permissions: [
              {
                actions: PermissionAction.READ,
                resources: PermissionResource.SpendAreas,
              },
              {
                actions: PermissionAction.READ,
                resources: PermissionResource.ActionItems,
              },
              {
                actions: PermissionAction.CREATE,
                resources: PermissionResource.SpendAreas,
              },
            ],
            operator: 'OR',
          },
        },
        {
          href: paths.documents.any,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Documents,
            },
          },
        },
        {
          href: paths.locations,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Locations,
            },
          },
        },

        ...(showReportsDirectory
          ? [{ href: paths.reports.root }, { href: paths.reports.tab }]
          : []),
        {
          href: ticketPaths.root,
          access: {
            permissions: [
              {
                actions: PermissionAction.READ,
                resources: PermissionResource.Tickets,
              },
              {
                actions: PermissionAction.CREATE,
                resources: PermissionResource.Tickets,
              },
            ],
            operator: 'OR',
          },
        },
        {
          href: ticketPaths.tab,
          access: {
            permissions: [
              {
                actions: PermissionAction.READ,
                resources: PermissionResource.Tickets,
              },
              {
                actions: PermissionAction.CREATE,
                resources: PermissionResource.Tickets,
              },
            ],
            operator: 'OR',
          },
        },
        {
          href: ticketPaths.new,
          access: {
            permissions: {
              actions: PermissionAction.CREATE,
              resources: PermissionResource.Tickets,
            },
          },
        },
        {
          href: ticketPaths.details,
          access: {
            permissions: [
              {
                actions: PermissionAction.READ,
                resources: PermissionResource.Tickets,
              },
              {
                actions: PermissionAction.CREATE,
                resources: PermissionResource.Tickets,
              },
            ],
            operator: 'OR',
          },
        },
        {
          href: ticketPaths.detailsWithTab,
          access: {
            permissions: [
              {
                actions: PermissionAction.READ,
                resources: PermissionResource.Tickets,
              },
              {
                actions: PermissionAction.CREATE,
                resources: PermissionResource.Tickets,
              },
            ],
            operator: 'OR',
          },
        },
        {
          href: paths.upcomingEvents,
        },
        {
          href: paths.chat,
        },
        {
          href: paths.notFound,
        },
        {
          href: paths.teamMembers.root,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Teams,
            },
          },
        },
        {
          href: paths.teamMembers.tab,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Teams,
            },
          },
        },
        {
          href: paths.account,
        },
        ...(hasBillingAccess
          ? [
              {
                href: paths.billing.root,
              },
              {
                href: paths.billing.tab,
              },
            ]
          : []),
        {
          href: paths.vendors.root,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Vendors,
            },
          },
        },
        {
          href: paths.vendors.tab,
          access: {
            permissions: {
              actions: PermissionAction.READ,
              resources: PermissionResource.Vendors,
            },
          },
        },
        {
          href: paths.billPay,
        },
        {
          href: paths.contractTerms,
        },
        {
          href: paths.help,
        },
        {
          href: ABOUTSIB_URL,
        },
      ].filter(
        ({ access }) =>
          !access || canAccess(access as Parameters<typeof canAccess>[0])
      ),
    [
      canAccess,
      hasBillingAccess,
      hasChildren,
      showReportsDirectory,
      ticketPaths,
    ]
  );

  const firstRouteAvailable = useMemo(
    () => availableRoutes[0].href,
    [availableRoutes]
  );

  const isRouteAvailable = useCallback(
    (path: string) => {
      const isReportsPath = [paths.reports.root, paths.reports.tab].some(
        reportPath => Boolean(matchPath({ path: reportPath, end: true }, path))
      );

      if (
        isOrganizationDataLoading ||
        (isReportsPath && arePowerBiReportsLoading)
      )
        return true;

      return (
        availableRoutes.some(route => route.href === path) ||
        availableRoutes.some(
          route =>
            matchPath({ path }, route.href) ||
            matchPath({ path: route.href }, path)
        )
      );
    },
    [arePowerBiReportsLoading, availableRoutes, isOrganizationDataLoading]
  );

  return {
    availableRoutes,
    isRouteAvailable,
    firstRouteAvailable,
    isOrganizationDataLoading,
  };
};
